import React, { FC } from 'react';
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Chip,
  Stack,
  Typography,
  Tooltip,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useUsers } from '../contexts/users.context';
import { UserType } from '../users.interfaces';
import {
  getTranslatedStatusOptions,
  getTranslatedParentRelationOptions,
  getTranslatedVisitorStatusOptions,
  getTranslatedGuestTypeOptions,
} from '../users.helpers';
import { useTranslation } from 'src/hooks/useTranslationHook';

interface UsersFiltersProps {
  onFilterChange: (filterKey: string, value: any) => void;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onResetFilters: () => void;
}

const UsersFilters: FC<UsersFiltersProps> = ({
  onFilterChange,
  onSearchChange,
  onResetFilters,
}) => {
  const { translateText } = useTranslation();
  const { state, setSearchQuery } = useUsers();

  // Get type-specific filter options
  const getTypeSpecificFilters = () => {
    switch (state.currentUserType) {
      case UserType.PARENTS:
        return (
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{translateText('users.filters.relation')}</InputLabel>
              <Select
                value={state.filters.relation || 'all'}
                label={translateText('users.filters.relation')}
                onChange={(e) => onFilterChange('relation', e.target.value)}
                data-testid="users-relation-filter"
              >
                {getTranslatedParentRelationOptions(translateText).map(
                  (option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Grid>
        );
      case UserType.VISITORS:
        return (
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>
                {translateText('users.filters.visitStatus')}
              </InputLabel>
              <Select
                value={state.filters.visitStatus || 'all'}
                label={translateText('users.filters.visitStatus')}
                onChange={(e) => onFilterChange('visitStatus', e.target.value)}
                data-testid="users-visit-status-filter"
              >
                {getTranslatedVisitorStatusOptions(translateText).map(
                  (option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Grid>
        );
      case UserType.GUESTS:
        return (
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>
                {translateText('users.filters.guestType')}
              </InputLabel>
              <Select
                value={state.filters.guestType || 'all'}
                label={translateText('users.filters.guestType')}
                onChange={(e) => onFilterChange('guestType', e.target.value)}
                data-testid="users-guest-type-filter"
              >
                {getTranslatedGuestTypeOptions(translateText).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <Grid item xs={12} sx={{ px: { xs: 1, sm: 3 }, mb: { xs: 2, sm: 3 } }} data-testid="users-filters">
      <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
        {/* Search */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            size="small"
            placeholder={translateText('users.filters.searchPlaceholder')}
            value={state.filters.searchQuery}
            onChange={onSearchChange}
            sx={{
              '& .MuiInputBase-root': {
                minHeight: { xs: '44px', sm: 'auto' },
                fontSize: { xs: '16px', sm: 'inherit' },
              },
            }}
            data-testid="users-search-input"
            InputProps={{
              startAdornment: (
                <Search sx={{ mr: 1, color: 'text.secondary' }} />
              ),
            }}
          />
        </Grid>

        {/* Status Filter */}
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>{translateText('users.filters.status')}</InputLabel>
            <Select
              value={state.filters.status}
              label={translateText('users.filters.status')}
              onChange={(e) => onFilterChange('status', e.target.value)}
              sx={{
                minHeight: { xs: '44px', sm: 'auto' },
                fontSize: { xs: '16px', sm: 'inherit' },
              }}
              data-testid="users-status-filter"
            >
              {getTranslatedStatusOptions(translateText).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Type-specific Filter */}
        {getTypeSpecificFilters()}

        {/* Filter Actions */}
        <Grid item xs={12} sm={12} md={3}>
          <Stack direction="row" spacing={1}>
            <Tooltip title={translateText('users.filters.resetFilters')}>
              <Button
                variant="outlined"
                size="small"
                onClick={onResetFilters}
                disabled={state.isLoading}
                sx={{
                  minHeight: { xs: '44px', sm: 'auto' },
                  width: { xs: '100%', sm: 'auto' },
                }}
                data-testid="users-reset-filters-button"
              >
                {translateText('users.filters.reset')}
              </Button>
            </Tooltip>
          </Stack>
        </Grid>
      </Grid>

      {/* Active Filters Display */}
      {(state.filters.status !== 'all' ||
        state.filters.relation !== 'all' ||
        state.filters.visitStatus !== 'all' ||
        state.filters.guestType !== 'all' ||
        state.filters.searchQuery) && (
        <Box sx={{ mt: { xs: 1, sm: 2 } }}>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: { xs: 0.5, sm: 1 },
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
            }}
          >
            {translateText('users.filters.activeFilters')}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: { xs: 0.5, sm: 1 } }}>
            {state.filters.status !== 'all' && (
              <Chip
                label={`${translateText('users.filters.statusLabel')} ${state.filters.status}`}
                size="small"
                onDelete={() => onFilterChange('status', 'all')}
              />
            )}
            {state.filters.relation && state.filters.relation !== 'all' && (
              <Chip
                label={`${translateText('users.filters.relationLabel')} ${state.filters.relation}`}
                size="small"
                onDelete={() => onFilterChange('relation', 'all')}
              />
            )}
            {state.filters.visitStatus &&
              state.filters.visitStatus !== 'all' && (
                <Chip
                  label={`${translateText('users.filters.visitLabel')} ${state.filters.visitStatus}`}
                  size="small"
                  onDelete={() => onFilterChange('visitStatus', 'all')}
                />
              )}
            {state.filters.guestType && state.filters.guestType !== 'all' && (
              <Chip
                label={`${translateText('users.filters.typeLabel')} ${state.filters.guestType}`}
                size="small"
                onDelete={() => onFilterChange('guestType', 'all')}
              />
            )}
            {state.filters.searchQuery && (
              <Chip
                label={`${translateText('users.filters.searchLabel')} "${state.filters.searchQuery}"`}
                size="small"
                onDelete={() => setSearchQuery('')}
              />
            )}
          </Stack>
        </Box>
      )}
    </Grid>
  );
};

export default UsersFilters;
